import { Inject, Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, catchError, combineLatest, distinctUntilChanged, EMPTY, filter, finalize, map, Observable, of, shareReplay, startWith, Subject, switchMap, take, takeUntil, tap, withLatestFrom } from 'rxjs';
import { AuthService } from '@app/modules/auth/services/auth.service';
import { BusinessProfileService } from '@modules/account-settings/pages/business-profile/services/business-profile.service';
import { BillingAddressService } from '@modules/address/services/billing-address.service';
import { BillingAddress } from '@modules/address/models/billing-address.model';
import { Job } from '@modules/job/models/job.model';
import { Order } from '@modules/order/models/order.model';
import {
  PaymentCheckoutContextResult,
  PaymentProviderNameEnum,
  PreparePaymentInput,
  PromotionApplicationCheckResultType,
  PromotionType,
  UploadProofOfPaymentInput,
} from '@generated/graphql';
import {
  AddressCardComponentConfig,
  addressCardComponentConfig,
} from '@shared/component/common/address-card/address-card.component';
import { OrderData } from '@shared/component/payments/components/order-summary/order-summary.type';
import { PaymentService } from '@shared/component/payments/services/payment.service';
import { PromotionService } from '@modules/promotion/services/promotion.service';
import { Promotion } from '@modules/promotion/models/promotion.model';
import { OrderService } from '@modules/order/services/order.service';
import { PaymentProviderService } from '@modules/payment/services/payment-provider.service';
import { ErrorService } from '@shared/services/error.service';
import { Router } from '@angular/router';
import { APP_ROUTES, AppRoutes } from '@config/app-routes.config';
import { SnackBarService } from '@shared/services/snack-bar.service';
import { ScreenSpinnerService } from '@shared/component/full-screen-spinner/screen-spinner.service';

interface BillingPreviewState {
  rawBillingPreview: PaymentCheckoutContextResult['paymentCheckoutContext'] | null;
  billingPreview: PaymentCheckoutContextResult['paymentCheckoutContext'] | null;
  loadingBillingPreview: boolean;
  hasBillingPreviewLoadError: boolean;
  billingPreviewSource: 'persisted' | 'prepared' | null;
}

interface PromotionCheckState {
  isCheckingPromotion: boolean;
  promotionApplicationCheckResult: PromotionApplicationCheckResultType | null;
}

export type WireTransferSubmissionResult =
  | { status: 'success' }
  | { status: 'failed' }
  | { status: 'validation_error'; message: string }
  | { status: 'error' };

export interface CheckoutPageViewModel extends BillingPreviewState {
  orderData: OrderData | null;
  currentOrder: Order | null;
  shouldShowPromoCode: boolean;
}

export interface PaymentMethodSubmitParams {
  isPaymentLocked: boolean;
  selectedPaymentProvider: PaymentProviderNameEnum | null;
  order: Order | null;
  wireTransferFile: File | null;
  redirectUrl?: any[];
  redirectQueryParams?: Record<string, string>;
  shouldRedirect?: boolean;
}

const INITIAL_BILLING_PREVIEW_STATE: BillingPreviewState = {
  rawBillingPreview: null,
  billingPreview: null,
  loadingBillingPreview: false,
  hasBillingPreviewLoadError: false,
  billingPreviewSource: null,
};

const INITIAL_PROMOTION_CHECK_STATE: PromotionCheckState = {
  isCheckingPromotion: false,
  promotionApplicationCheckResult: null,
};

@Injectable()
export class CheckoutFlowFacadeService implements OnDestroy {
  readonly businessProfile$ = this.businessProfileService.activeEntity$;
  readonly activeUser$ = this.authService.activeUser$;
  readonly returnUrl$ = this.orderService.returnUrl$;
  readonly paymentProviderOptions$ = this.paymentProviderService.ensureLoaded$().pipe(
    switchMap(() => this.paymentProviderService.paymentProviderOptions$),
    shareReplay({ bufferSize: 1, refCount: true }),
  );
  readonly selectedBillingAddress$ = this.billingAddressService.selectedBillingAddress$;
  readonly activeBillingAddress$ = this.billingAddressService.activeEntity$;
  readonly activePromotion$ = this.promotionService.activeEntity$;
  readonly activePromotionId$ = this.activePromotion$.pipe(
    map((promotion) => promotion?.id ?? null),
    distinctUntilChanged(),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  private readonly destroy$ = new Subject<void>();
  private readonly orderDataSubject = new BehaviorSubject<OrderData | null>(null);
  private readonly billingPreviewStateSubject = new BehaviorSubject<BillingPreviewState>(INITIAL_BILLING_PREVIEW_STATE);
  private readonly promotionCheckStateSubject = new BehaviorSubject<PromotionCheckState>(INITIAL_PROMOTION_CHECK_STATE);
  private readonly selectedPaymentProviderSubject = new BehaviorSubject<PaymentProviderNameEnum | null>(null);
  private readonly selectedPromotionIdSubject = new BehaviorSubject<string | null>(null);
  private readonly wireTransferSubmittingSubject = new BehaviorSubject<boolean>(false);
  private activePromotion: Promotion | null = null;
  private promotionSelectionOverrideOrderId: string | null = null;

  readonly orderData$ = this.orderDataSubject.asObservable();
  readonly currentOrder$ = combineLatest([
    this.orderData$,
    this.orderService.activeEntity$.pipe(startWith(null)),
  ]).pipe(
    map(([orderData, activeOrder]) => {
      const inputOrder = orderData?.order ?? null;

      if (activeOrder?.id && activeOrder.id === inputOrder?.id) {
        return activeOrder;
      }

      return inputOrder;
    }),
    distinctUntilChanged((previous, current) => this.buildOrderSignature(previous) === this.buildOrderSignature(current)),
    shareReplay({ bufferSize: 1, refCount: true }),
  );
  readonly shouldShowPromoCode$ = this.orderData$.pipe(
    map((orderData) => {
      const orderable = orderData?.order?.orderable ?? orderData;
      return orderData instanceof Job || orderable instanceof Job;
    }),
    distinctUntilChanged(),
    shareReplay({ bufferSize: 1, refCount: true }),
  );
  readonly addressCardComponentConfig$ = this.currentOrder$.pipe(
    map((order): AddressCardComponentConfig => ({
      ...addressCardComponentConfig,
      showEditAction: Boolean(order?.payment?.canMakePayment),
    })),
    shareReplay({ bufferSize: 1, refCount: true }),
  );
  readonly vm$ = combineLatest([
    this.orderData$,
    this.currentOrder$,
    this.shouldShowPromoCode$,
    this.billingPreviewStateSubject.asObservable(),
  ]).pipe(
    map(([orderData, currentOrder, shouldShowPromoCode, billingPreviewState]): CheckoutPageViewModel => ({
      orderData,
      currentOrder,
      shouldShowPromoCode,
      ...billingPreviewState,
    })),
    shareReplay({ bufferSize: 1, refCount: true }),
  );
  readonly isCheckingPromotion$ = this.promotionCheckStateSubject.asObservable().pipe(
    map((state) => state.isCheckingPromotion),
    distinctUntilChanged(),
    shareReplay({ bufferSize: 1, refCount: true }),
  );
  readonly promotionApplicationCheckResult$ = this.promotionCheckStateSubject.asObservable().pipe(
    map((state) => state.promotionApplicationCheckResult),
    distinctUntilChanged(),
    shareReplay({ bufferSize: 1, refCount: true }),
  );
  readonly selectedPaymentProvider$ = this.selectedPaymentProviderSubject.asObservable().pipe(
    distinctUntilChanged(),
    shareReplay({ bufferSize: 1, refCount: true }),
  );
  readonly selectedPromotionId$ = this.selectedPromotionIdSubject.asObservable().pipe(
    distinctUntilChanged(),
    shareReplay({ bufferSize: 1, refCount: true }),
  );
  readonly wireTransferSubmitting$ = this.wireTransferSubmittingSubject.asObservable().pipe(
    distinctUntilChanged(),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  constructor(
    @Inject(APP_ROUTES) private readonly appRoutes: AppRoutes,
    private readonly router: Router,
    private readonly businessProfileService: BusinessProfileService,
    private readonly billingAddressService: BillingAddressService,
    private readonly authService: AuthService,
    private readonly paymentService: PaymentService,
    private readonly promotionService: PromotionService,
    private readonly orderService: OrderService,
    private readonly paymentProviderService: PaymentProviderService,
    private readonly errorService: ErrorService,
    private readonly snackBarService: SnackBarService,
    private readonly screenSpinnerService: ScreenSpinnerService,
  ) {
    this.activePromotion$.pipe(
      tap((promotion) => {
        this.activePromotion = promotion;
      }),
      takeUntil(this.destroy$),
    ).subscribe();

    this.syncInitialBillingAddress();
    this.syncDefaultPaymentProvider();
    this.syncWireTransferSpinner();
    this.watchOrderChanges();
    this.watchPreparedCheckoutContext();
  }

  ngOnDestroy(): void {
    this.screenSpinnerService.stopLoading();
    this.destroy$.next();
    this.destroy$.complete();
    this.orderDataSubject.complete();
    this.billingPreviewStateSubject.complete();
    this.promotionCheckStateSubject.complete();
    this.selectedPaymentProviderSubject.complete();
    this.selectedPromotionIdSubject.complete();
    this.wireTransferSubmittingSubject.complete();
  }

  setOrderData(orderData: OrderData | null): void {
    if (this.resolveOrderId(this.orderDataSubject.value) !== this.resolveOrderId(orderData)) {
      this.promotionSelectionOverrideOrderId = null;
    }

    this.orderDataSubject.next(orderData);
  }

  setSelectedBillingAddress(address: BillingAddress | null): void {
    this.billingAddressService.setSelectedBillingAddress(address);
  }

  get selectedPaymentProvider(): PaymentProviderNameEnum | null {
    return this.selectedPaymentProviderSubject.value;
  }

  setSelectedPaymentProvider(provider: PaymentProviderNameEnum | null): void {
    this.selectedPaymentProviderSubject.next(provider ?? null);
  }

  preparePayment$(input: Omit<PreparePaymentInput, 'billingAddressId' | 'promotionId'>): ReturnType<PaymentService['preparePayment']> {
    return combineLatest([
      this.selectedBillingAddress$.pipe(take(1)),
      this.selectedPromotionId$.pipe(take(1)),
    ]).pipe(
      switchMap(([billingAddress, promotionId]) =>
        this.paymentService.preparePayment({
          ...input,
          billingAddressId: billingAddress instanceof BillingAddress ? billingAddress.id : null,
          promotionId,
        })
      ),
    );
  }

  uploadProofOfPayment$(input: Omit<UploadProofOfPaymentInput, 'billingAddressId'>): ReturnType<PaymentService['uploadProofOfPayment']> {
    return this.selectedBillingAddress$.pipe(
      take(1),
      switchMap((billingAddress) => {
        if (!(billingAddress instanceof BillingAddress)) {
          return EMPTY;
        }

        return this.paymentService.uploadProofOfPayment({
          ...input,
          billingAddressId: billingAddress.id,
        });
      }),
    );
  }

  prepareWireTransferAndUpload$(input: Pick<UploadProofOfPaymentInput, 'orderId' | 'file'>): ReturnType<PaymentService['uploadProofOfPayment']> {
    const wireTransferInput = {
      orderId: input.orderId,
      file: input.file,
    };

    this.wireTransferSubmittingSubject.next(true);

    return this.preparePayment$({
      orderId: wireTransferInput.orderId,
      provider: PaymentProviderNameEnum.WireTransfer,
    }).pipe(
      switchMap(() => this.uploadProofOfPayment$(wireTransferInput)),
      finalize(() => this.wireTransferSubmittingSubject.next(false)),
    );
  }

  submitWireTransfer$(input: Pick<UploadProofOfPaymentInput, 'orderId' | 'file'> & { paymentId?: string | null }): Observable<WireTransferSubmissionResult> {
    this.paymentService.markOrderProcessing(input.orderId);
    this.paymentService.markOrderAwaitingRefresh(input.orderId);

    if (input.paymentId) {
      this.paymentService.markProcessing(input.paymentId);
    }

    return this.prepareWireTransferAndUpload$(input).pipe(
      map((result) => {
        if (!result) {
          this.paymentService.clearOrderProcessing(input.orderId);
          this.paymentService.clearOrderAwaitingRefresh(input.orderId);
          if (input.paymentId) {
            this.paymentService.clearProcessing(input.paymentId);
          }
          return { status: 'failed' } as WireTransferSubmissionResult;
        }

        if (input.paymentId) {
          this.paymentService.paid$.next({ paymentId: input.paymentId });
        }

        return { status: 'success' } as WireTransferSubmissionResult;
      }),
      catchError((error) => {
        const errorMessages = this.errorService.getValidationErrorMessages(error?.graphQLErrors ?? []);

        this.paymentService.clearOrderProcessing(input.orderId);
        this.paymentService.clearOrderAwaitingRefresh(input.orderId);
        if (input.paymentId) {
          this.paymentService.clearProcessing(input.paymentId);
        }

        if (errorMessages.length) {
          return of({
            status: 'validation_error',
            message: errorMessages[0],
          } as WireTransferSubmissionResult);
        }

        return of({ status: 'error' } as WireTransferSubmissionResult);
      }),
    );
  }

  submitWireTransferAndHandleUi$(
    input: Pick<UploadProofOfPaymentInput, 'orderId' | 'file'> & {
      paymentId?: string | null;
      redirectUrl?: any[];
      redirectQueryParams?: Record<string, string>;
      shouldRedirect?: boolean;
    },
  ): Observable<WireTransferSubmissionResult> {
    const wireTransferInput = {
      orderId: input.orderId,
      file: input.file,
      paymentId: input.paymentId,
    };

    return this.submitWireTransfer$(wireTransferInput).pipe(
      tap((result) => {
        if (result.status === 'validation_error') {
          this.snackBarService.pushErrorAlert(result.message);
          return;
        }

        if (result.status === 'failed') {
          this.snackBarService.pushSubmitPaymentFailMessage();
          return;
        }

        if (result.status === 'error') {
          this.snackBarService.pushErrorMessage();
          return;
        }

        this.snackBarService.pushSubmitPaymentSuccessMessage();

        if (input.shouldRedirect) {
          void this.router.navigate(input.redirectUrl ?? this.appRoutes.dashboard(), {
            queryParams: input.redirectQueryParams,
          });
        }
      }),
    );
  }

  submitSelectedPaymentMethod$(params: PaymentMethodSubmitParams): Observable<void> {
    if (params.isPaymentLocked) {
      this.snackBarService.pushWarningAlert('Payment is currently locked. Please wait for confirmation.');
      return EMPTY;
    }

    if (!params.order?.payment?.canMakePayment) {
      this.snackBarService.pushWarningAlert('Payment is not available at the moment');
      return EMPTY;
    }

    if (params.selectedPaymentProvider === PaymentProviderNameEnum.WireTransfer) {
      return this.submitWireTransferAndHandleUi$({
        orderId: params.order.id,
        file: params.wireTransferFile,
        paymentId: params.order.payment?.id,
        redirectUrl: params.redirectUrl,
        redirectQueryParams: params.redirectQueryParams,
        shouldRedirect: params.shouldRedirect,
      }).pipe(
        map(() => void 0),
      );
    }

    this.snackBarService.pushWarningAlert('Payment method not available');

    return EMPTY;
  }

  applyPromoCode$(orderId: string, promoCode: string): Observable<PromotionType | null> {
    if (!orderId || !promoCode) {
      return EMPTY;
    }

    this.clearActivePromotion();
    this.promotionCheckStateSubject.next({
      isCheckingPromotion: true,
      promotionApplicationCheckResult: null,
    });

    return this.promotionService.checkCanApplyPromotionByCode(promoCode, orderId).pipe(
      tap((promotionApplicationCheckResult) => {
        this.promotionCheckStateSubject.next({
          isCheckingPromotion: true,
          promotionApplicationCheckResult,
        });
      }),
      switchMap((promotionApplicationCheckResult) => {
        if (!promotionApplicationCheckResult.isValid) {
          return of(null);
        }

        return this.promotionService.findByCode(promoCode);
      }),
      tap((promotion) => {
        if (!promotion) {
          return;
        }

        this.promotionService.dispatchAddOrUpdateThenSelectEntityAction(promotion);
        this.selectedPromotionIdSubject.next(promotion.id);
        this.promotionSelectionOverrideOrderId = orderId;
      }),
      finalize(() => {
        const currentState = this.promotionCheckStateSubject.value;
        this.promotionCheckStateSubject.next({
          ...currentState,
          isCheckingPromotion: false,
        });
      }),
    );
  }

  removePromotion(): void {
    this.promotionCheckStateSubject.next(INITIAL_PROMOTION_CHECK_STATE);
    this.clearActivePromotion();
    this.selectedPromotionIdSubject.next(null);
    this.promotionSelectionOverrideOrderId = this.resolveOrderId(this.orderDataSubject.value);
  }

  resetPromotionState(): void {
    this.removePromotion();
  }

  resetPromotionCheckState(): void {
    this.promotionCheckStateSubject.next(INITIAL_PROMOTION_CHECK_STATE);
  }

  private syncInitialBillingAddress(): void {
    combineLatest([
      this.activeBillingAddress$,
      this.selectedBillingAddress$,
    ]).pipe(
      tap(([activeBillingAddress, selectedBillingAddress]) => {
        if (selectedBillingAddress instanceof BillingAddress) {
          return;
        }

        if (activeBillingAddress instanceof BillingAddress) {
          this.setSelectedBillingAddress(activeBillingAddress);
        }
      }),
      takeUntil(this.destroy$),
    ).subscribe();
  }

  private syncDefaultPaymentProvider(): void {
    combineLatest([
      this.paymentProviderOptions$,
      this.selectedPaymentProvider$,
    ]).pipe(
      tap(([paymentProviderOptions, selectedPaymentProvider]) => {
        if (!paymentProviderOptions.length || selectedPaymentProvider) {
          return;
        }

        this.setSelectedPaymentProvider(paymentProviderOptions[0].value);
      }),
      takeUntil(this.destroy$),
    ).subscribe();
  }

  private watchOrderChanges(): void {
    this.currentOrder$.pipe(
      tap((order) => {
        if (!order) {
          this.selectedPromotionIdSubject.next(null);
          this.billingPreviewStateSubject.next(INITIAL_BILLING_PREVIEW_STATE);
          return;
        }

        this.syncSelectedBillingAddressForOrder(order);
        this.syncSelectedPromotionForOrder(order);
      }),
      takeUntil(this.destroy$),
    ).subscribe();

    combineLatest([
      this.currentOrder$,
      this.selectedPromotionId$,
      this.paymentService.processingOrderIds$,
      this.paymentService.awaitingRefreshOrderIds$,
    ]).pipe(
      tap(([order, , processingOrderIds, awaitingRefreshOrderIds]) => {
        if (!order) {
          this.billingPreviewStateSubject.next(INITIAL_BILLING_PREVIEW_STATE);
          return;
        }

        if (!this.canLoadCheckoutContext(order, processingOrderIds, awaitingRefreshOrderIds)) {
          const currentState = this.billingPreviewStateSubject.value;

          this.billingPreviewStateSubject.next({
            ...currentState,
            loadingBillingPreview: false,
            hasBillingPreviewLoadError: false,
          });
          return;
        }

        this.billingPreviewStateSubject.next({
          rawBillingPreview: null,
          billingPreview: null,
          loadingBillingPreview: true,
          hasBillingPreviewLoadError: false,
          billingPreviewSource: 'persisted',
        });
      }),
      switchMap(([order, promotionId, processingOrderIds, awaitingRefreshOrderIds]) => {
        if (!order?.id || !this.canLoadCheckoutContext(order, processingOrderIds, awaitingRefreshOrderIds)) {
          return EMPTY;
        }

        return this.paymentService.getCheckoutContext(order.id, promotionId).pipe(
          tap((billingPreview) => {
            this.setBillingPreviewState(
              billingPreview ?? null,
              'persisted',
              false,
              !billingPreview,
            );
          }),
          catchError(() => {
            this.billingPreviewStateSubject.next({
              rawBillingPreview: null,
              billingPreview: null,
              loadingBillingPreview: false,
              hasBillingPreviewLoadError: true,
              billingPreviewSource: 'persisted',
            });

            return EMPTY;
          }),
        );
      }),
      takeUntil(this.destroy$),
    ).subscribe();
  }

  private watchPreparedCheckoutContext(): void {
    this.paymentService.preparedCheckoutContext$.pipe(
      withLatestFrom(this.currentOrder$),
      filter(([preparedContext, currentOrder]) => !!currentOrder?.id && preparedContext.orderId === currentOrder.id),
      tap(([preparedContext]) => {
        this.setBillingPreviewState(
          preparedContext.checkoutContext ?? null,
          'prepared',
          false,
          !preparedContext.checkoutContext,
        );
      }),
      takeUntil(this.destroy$),
    ).subscribe();
  }

  private syncWireTransferSpinner(): void {
    this.wireTransferSubmitting$.pipe(
      tap((isSubmitting) => {
        if (isSubmitting) {
          this.screenSpinnerService.loading();
          return;
        }

        this.screenSpinnerService.stopLoading();
      }),
      takeUntil(this.destroy$),
    ).subscribe();
  }

  private setBillingPreviewState(
    rawBillingPreview: PaymentCheckoutContextResult['paymentCheckoutContext'] | null,
    billingPreviewSource: BillingPreviewState['billingPreviewSource'],
    loadingBillingPreview: boolean,
    hasBillingPreviewLoadError: boolean,
  ): void {
    this.billingPreviewStateSubject.next({
      rawBillingPreview,
      billingPreview: rawBillingPreview,
      loadingBillingPreview,
      hasBillingPreviewLoadError,
      billingPreviewSource,
    });
  }

  private canLoadCheckoutContext(
    order: Order | null,
    processingOrderIds: Set<string> = new Set(),
    awaitingRefreshOrderIds: Set<string> = new Set(),
  ): boolean {
    if (!order?.id || !order.payment?.canMakePayment) {
      return false;
    }

    if (processingOrderIds.has(order.id) || awaitingRefreshOrderIds.has(order.id)) {
      return false;
    }

    return true;
  }

  private syncSelectedBillingAddressForOrder(order: Order): void {
    combineLatest([
      this.selectedBillingAddress$.pipe(take(1)),
      this.billingAddressService.getAllAddress$.pipe(take(1)),
      this.activeBillingAddress$.pipe(take(1)),
    ]).pipe(
      tap(([selectedAddress, billingAddresses, activeBillingAddress]) => {
        if (selectedAddress instanceof BillingAddress) {
          return;
        }

        const orderBillingAddressId = order.orderDetail?.address?.id;
        const matchedBillingAddress = orderBillingAddressId
          ? billingAddresses.find((address) => address.id === orderBillingAddressId)
          : null;
        const resolvedBillingAddress = matchedBillingAddress
          ?? (activeBillingAddress instanceof BillingAddress ? activeBillingAddress : null);

        if (resolvedBillingAddress instanceof BillingAddress) {
          this.setSelectedBillingAddress(resolvedBillingAddress);
        }
      }),
      takeUntil(this.destroy$),
    ).subscribe();
  }

  private syncSelectedPromotionForOrder(order: Order): void {
    if (this.promotionSelectionOverrideOrderId === order.id) {
      return;
    }

    const orderPromotion = order.promotion ?? null;

    if (orderPromotion?.id) {
      this.promotionService.dispatchAddOrUpdateThenSelectEntityAction(orderPromotion);
      this.selectedPromotionIdSubject.next(orderPromotion.id);
      return;
    }

    this.clearActivePromotion();
    this.selectedPromotionIdSubject.next(null);
  }

  private clearActivePromotion(): void {
    if (!this.activePromotion?.id) {
      return;
    }

    this.promotionService.dispatchDeleteEntityAction(this.activePromotion.id);
  }

  private buildOrderSignature(order: Order | null): string {
    if (!order?.id) {
      return '';
    }

    const updatedAt = order.updatedAt ? new Date(order.updatedAt).getTime() : '';
    const paymentStatus = order.payment?.status ?? '';

    return `${order.id}|${updatedAt}|${paymentStatus}`;
  }

  private resolveOrderId(orderData: OrderData | null): string | null {
    return orderData?.order?.id ?? null;
  }
}
